import { saveAs } from "file-saver";
import docx from "remark-docx";
import markdown from "remark-parse";
import { unified } from "unified";
import { jsPDF } from 'jspdf';
import { marked } from "marked";

export enum DownloadFileType {
    pdf,
    docx
}

export const downloadFile = async (markdownContent: string, filename: string, fileType: DownloadFileType): Promise<void> => {
    switch (fileType) {
        case DownloadFileType.docx:
            const docxFile = await createDocx(markdownContent, filename);
            saveAs(docxFile, `${filename}.docx`);
            break;
        case DownloadFileType.pdf:
            const pdfFile = await createPdf(markdownContent);
            saveAs(pdfFile, `${filename}.pdf`);
            break;
    }
}

// Existing function for creating DOCX
export const createDocx = async (markdownContent: string, filename: string): Promise<File> => {
    const processor = unified().use(markdown).use(docx, { output: "blob" });
    const doc = await processor.process(markdownContent);
    const blob = await doc.result as Blob;
    return new File([blob], filename);
}

// New function for creating PDF using addFont
export const createPdf = async (
    markdownContent: string,
): Promise<Blob> => {
    return new Promise(async (resolve, reject) => {
        try {
            // 1. Convert Markdown to HTML
            const rawHtml = await marked.parse(markdownContent);

            // 2. Create jsPDF instance
            const pdf = new jsPDF();

            // 3. Register fonts using addFont
            pdf.addFont('/fonts/Inter-Regular.ttf', 'Inter', 'normal');
            pdf.addFont('/fonts/Inter-Bold.ttf', 'Inter', 'bold');

            // 4. Prepare HTML content with CSS body padding
            const htmlContent = `
          <html>
            <head>
              <style>
                /* Base styles */
                body {
                  font-family: Inter, sans-serif;
                  font-size: 12px;
                  font-weight: normal;
                  line-height: 1.4;
                  /* Add padding to body instead of using jsPDF margin */
                  /* x:15 handles left, y:15 handles top */
                  padding-bottom: 15px; /* Add desired bottom space */
                  /* width:180 handles right space */
                }
                p, li {
                  font-family: Inter, sans-serif;
                  font-weight: normal;
                  page-break-inside: avoid;
                  line-height: 1.4;
                }
                h1, h2, h3, h4, h5, h6, strong, b {
                  font-family: Inter, sans-serif;
                  font-weight: bold;
                  page-break-inside: avoid;
                  line-height: 1.4;
                }
                /* Keep alignment fix attempt, might still be needed */
                li strong {
                  /* display: inline-block; */ /* Revert if it didn't help */
                  vertical-align: baseline; /* Revert if it didn't help */
                }
              </style>
            </head>
            <body>
              ${rawHtml}
            </body>
          </html>
        `;

            // 5. Render HTML asynchronously using x, y
            pdf.html(htmlContent, {
                callback: (doc) => {
                    // 6. Once rendering is done, output as blob and resolve
                    const pdfBlob = doc.output('blob');
                    resolve(pdfBlob);
                },
                // Use x, y for positioning (fixes alignment)
                x: 0,
                y: 5,
                // Remove the margin option
                margin: [15, 15, 15, 15],
                autoPaging: 'text',
                // Set width based on page size and x offset
                width: 180, // e.g., 210mm (A4) - 15mm (x) - 15mm (desired right space)
                windowWidth: 650,
            });
        } catch (error) {
            console.error("Error creating PDF:", error);
            reject(error);
        }
    });
};
